import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { CheckoutRequestMetadata, PaymentMetadata } from '@shared/data-access/data-access-payment';
import { distinctUntilChanged, map, Observable, shareReplay, take } from 'rxjs';
import { PaymentScope, PaymentService, TPaymentScope } from '../../data-access/data-access-payment';
import { TokenStore } from '../../data-access/data-access-token';
import { getDeepestActivatedRouteSnapshot } from '../../utils/router.utils';
import { PaymentCompletedOverlayComponent } from '../payment-completed-overlay/payment-completed-overlay.component';
import { AuthStore } from '../../data-access/data-access-authentication';

@Component({
  selector: 'app-payment-handler',
  imports: [PaymentCompletedOverlayComponent],
  templateUrl: './payment-handler.component.html',
})
export class PaymentHandlerComponent {
  private readonly router = inject(Router);
  private readonly authStore = inject(AuthStore);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly paymentService = inject(PaymentService);
  private readonly tokenStore = inject(TokenStore);

  PaymentMetadata = PaymentMetadata;

  private routeDataQueryParams$ = this.router.events.pipe(
    map(() => getDeepestActivatedRouteSnapshot(this.router.routerState.snapshot.root)),
    map((route) => route.queryParams),
    takeUntilDestroyed(),
  );

  private checkoutMetadata$ = this.routeDataQueryParams$.pipe(
    distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
    map((data) => data as Partial<CheckoutRequestMetadata>),
    takeUntilDestroyed(),
    shareReplay(1),
  );

  private checkoutId$ = this.checkoutMetadata$.pipe(
    map((metadata) => metadata[PaymentMetadata.CHECKOUT_ID]),
    takeUntilDestroyed(),
  );

  checkoutMetadata = toSignal(this.checkoutMetadata$.pipe(take(1)));
  checkoutId = toSignal(this.checkoutId$);

  paymentScope = computed(() => this.checkoutMetadata()?.[PaymentMetadata.SCOPE] as TPaymentScope | undefined);
  tokens = computed(() => this.checkoutMetadata()?.[PaymentMetadata.TOKENS]);
  reportId = computed(() => this.checkoutMetadata()?.[PaymentMetadata.REPORT_ID]);

  showCheckoutCompletedOverlay = signal(false);

  private listenForConfirmCheckout = effect(() => {
    const checkoutId = this.checkoutId();
    const paymentScope = this.paymentScope();

    if (checkoutId && paymentScope) {
      untracked(() => this.confirmCheckout(checkoutId, paymentScope));
    }
  });

  private confirmCheckout(checkoutId: string, paymentScope: TPaymentScope): void {
    this.showCheckoutCompletedOverlay.set(false);

    switch (paymentScope) {
      case PaymentScope.PLAN: {
        this.paymentService.confirmCheckout(checkoutId).subscribe(() => {
          this.showCheckoutCompletedOverlay.set(true);

          this.authStore.load({ skipCache: true });
          this.router.navigate(['.'], { relativeTo: this.activatedRoute });
        });
        break;
      }
      case PaymentScope.TOP_UP: {
        this.paymentService.confirmCheckout(checkoutId).subscribe(() => {
          this.tokenStore.openBuyTokensDialog(true, this.tokens());

          this.authStore.load({ skipCache: true });
          this.router.navigate(['.'], { relativeTo: this.activatedRoute });
        });
        break;
      }
      case PaymentScope.REPORT_UPSELL: {
        const reportId = this.reportId();
        if (!reportId) {
          return;
        }
        this.paymentService.confirmReportCheckout(checkoutId, reportId).subscribe(() => {
          this.authStore.load({ skipCache: true });
          this.router.navigate(['/search'], { queryParams: { success: true } });
        });
        break;
      }
      default: {
        return;
      }
    }
  }
}
